import { useCallback, useEffect, useRef, useState } from 'react';

import { screenshotError } from '../screenshot';
import { SupportSubmissionRejection } from '../submissionRejection';
import type {
  SupportDraft,
  SupportReceipt as SupportRequestResponse,
  SupportRequest as SupportRequestCreate,
  SupportView,
} from '../types';

export interface SupportTransport {
  submit: (request: SupportRequestCreate) => Promise<SupportRequestResponse>;
  check: (requestId: string) => Promise<SupportRequestResponse>;
}

const emptyDraft = (): SupportDraft => ({ subject: '', message: '', screenshot: null });
const viewFor = (receipt: SupportRequestResponse): SupportView => {
  if (receipt.status === 'failed') return receipt.retry_allowed ? 'retryable-failure' : 'failure';
  return receipt.status === 'pending' ? 'sending' : receipt.status;
};

async function encodeScreenshot(
  file: File,
): Promise<NonNullable<SupportRequestCreate['attachment']>> {
  const error = screenshotError(file);
  if (error) throw new Error(error);
  // Narrow against the generated schema rather than widening a wire union.
  if (file.type !== 'image/png' && file.type !== 'image/jpeg' && file.type !== 'image/webp')
    throw new Error('Choose an image screenshot.');
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === 'string'
        ? resolve(reader.result.split(',')[1])
        : reject(new Error('Unable to read screenshot.'));
    reader.onerror = () => reject(new Error('Unable to read screenshot. Choose the file again.'));
    reader.readAsDataURL(file);
  });
  const extension = file.type === 'image/png' ? 'png' : file.type === 'image/jpeg' ? 'jpg' : 'webp';
  const basename =
    file.name
      .replace(/\.[^.]*$/, '')
      .replace(/[^\w .()-]/g, '_')
      .slice(0, 110)
      .trim() || 'screenshot';
  return { filename: `${basename}.${extension}`, content_type: file.type, data_base64: data };
}

/** Keeps the UUID and encoded payload together until confirmed delivery. No persisted draft. */
export function useSupportRequest(
  transport: SupportTransport,
  initialReplyEmail: string,
  pageArea: SupportRequestCreate['page_area'] = 'other',
) {
  const [draft, setDraft] = useState<SupportDraft>(emptyDraft);
  const [view, setView] = useState<SupportView>('compose');
  const [receipt, setReceipt] = useState<SupportRequestResponse | null>(null);
  const [requestId, setRequestId] = useState<string>();
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [checkRevision, setCheckRevision] = useState(0);
  const [preparationError, setPreparationError] = useState<string>();
  const payload = useRef<SupportRequestCreate | null>(null);
  const inFlight = useRef(false);
  const receiptRef = useRef<SupportRequestResponse | null>(null);
  const checks = useRef(0);
  const transportRef = useRef(transport);
  transportRef.current = transport;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const accept = useCallback((result: SupportRequestResponse) => {
    if (!mounted.current) return;
    if (result.request_id !== payload.current?.request_id) {
      receiptRef.current = null;
      setView('unknown');
      return;
    }
    receiptRef.current = result;
    setReceipt(result);
    // Keep ordinary confirmation inside Sending; surface only prolonged uncertainty.
    setView(result.status === 'pending' && checks.current >= 3 ? 'unknown' : viewFor(result));
  }, []);

  const deliver = useCallback(
    async (request: SupportRequestCreate) => {
      try {
        accept(await transportRef.current.submit(request));
      } catch (error) {
        if (!mounted.current) return;
        if (error instanceof SupportSubmissionRejection) {
          payload.current = null;
          receiptRef.current = null;
          checks.current = 0;
          setReceipt(null);
          setRequestId(undefined);
          setPreparationError(error.message);
          setView('compose');
        } else {
          // A dropped response cannot prove that Front did not receive the request.
          setView('unknown');
        }
      } finally {
        inFlight.current = false;
      }
    },
    [accept],
  );

  const submit = async () => {
    if (inFlight.current || payload.current || !draft.message.trim()) return;
    inFlight.current = true;
    setPreparationError(undefined);
    setView('sending');
    const snapshot = { ...draft };
    let request: SupportRequestCreate;
    try {
      request = {
        request_id: crypto.randomUUID(),
        subject: snapshot.subject.trim() || 'Support request',
        message: snapshot.message.trim(),
        page_area: pageArea,
        ...(snapshot.screenshot ? { attachment: await encodeScreenshot(snapshot.screenshot) } : {}),
      };
    } catch {
      inFlight.current = false;
      if (mounted.current) {
        setView('compose');
        setPreparationError('We couldn’t read the screenshot. Remove it or choose the file again.');
      }
      return;
    }
    if (!mounted.current) {
      inFlight.current = false;
      return;
    }
    payload.current = request;
    checks.current = 0;
    if (mounted.current) setRequestId(request.request_id);
    await deliver(request);
  };

  const retry = async () => {
    if (
      inFlight.current ||
      !payload.current ||
      receiptRef.current?.status !== 'failed' ||
      !receiptRef.current.retry_allowed
    )
      return;
    inFlight.current = true;
    setView('sending');
    // Clear permission before dispatch: an uncertain retry must not be retryable again.
    receiptRef.current = null;
    checks.current = 0;
    await deliver(payload.current);
  };

  const checkStatus = useCallback(async () => {
    if (inFlight.current || !payload.current) return;
    inFlight.current = true;
    setCheckingStatus(true);
    try {
      accept(await transportRef.current.check(payload.current.request_id));
    } catch {
      if (mounted.current) setView('unknown');
    } finally {
      inFlight.current = false;
      if (mounted.current) {
        setCheckingStatus(false);
        setCheckRevision(value => value + 1);
      }
    }
  }, [accept]);

  useEffect(() => {
    if (
      view !== 'sending' ||
      receipt?.status !== 'pending' ||
      checkingStatus ||
      checks.current >= 3
    )
      return;
    const timer = setTimeout(() => {
      checks.current += 1;
      void checkStatus();
    }, [1500, 3000, 5000][checks.current]);
    return () => clearTimeout(timer);
  }, [view, receipt, checkingStatus, checkStatus, checkRevision]);

  const newRequest = () => {
    if (view !== 'sent' || inFlight.current) return;
    payload.current = null;
    receiptRef.current = null;
    setReceipt(null);
    setRequestId(undefined);
    setDraft(emptyDraft());
    setView('compose');
  };

  return {
    draft,
    setDraft,
    view,
    requestId,
    checkingStatus,
    preparationError,
    replyEmail: receipt?.reply_email || initialReplyEmail,
    submit,
    retry,
    checkStatus,
    newRequest,
  };
}
